<template>
  <Section
    id="job-status"
    class="batch-import-job-status-section"
    :header="t('batchImport.jobStatus')"
  >
    <ProgressBar
      :mode="jobStatus?.isFinalState() ? 'determinate' : 'indeterminate'"
      :show-value="false"
      :value="jobStatus?.isFinalState() ? 100 : 0"
    />
    <div class="flex flex-column flex-gap-1 align-items-center m-3">
      <BatchImportJobStatusStepIcon
        :state="uploadState"
        :label="t('batchImport.uploadDocument')"
      />

      <BatchImportJobStatusStepIcon
        :state="processState"
        :label="t('batchImport.importRecords')"
      />
      <p class="job-stat">
        {{
          t(
            'batchImport.countRecordsFound',
            { count: jobStatusTotalRecords },
            jobStatusTotalRecords,
          )
        }}
      </p>
      <p class="job-stat">
        {{
          t(
            'batchImport.countImported',
            { count: jobStatusSuccessCount },
            jobStatusSuccessCount,
          )
        }}
      </p>
      <p class="job-stat">
        {{
          t(
            'batchImport.countErrors',
            { count: jobStatusErrorCount },
            jobStatusErrorCount,
          )
        }}
      </p>
      <div v-show="loadingErrorDocument || errorDocument">
        <template v-if="loadingErrorDocument">
          <i class="pi pi-spin pi-spinner" />
          {{ t('batchImport.loadingErrorDocument') }}
        </template>
        <template v-else>
          <a
            class="no-underline"
            :href="errorDocument?.signedUrl"
            target="_blank"
          >
            <RygenButton
              mode="tertiary"
              icon="pi pi-download"
              icon-pos="left"
              :severity="Severity.DANGER"
              :label="t('batchImport.downloadErrors')"
            />
          </a>
        </template>
      </div>
    </div>
  </Section>
</template>

<script lang="ts" setup>
import { computed, PropType, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { BatchJobExecutionDetails, Severity } from '@rygen/rygen-ui/model';

import { BatchImportStepState, DocumentReference } from '@/model';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';
import ProgressBar from 'primevue/progressbar';

import BatchImportJobStatusStepIcon from './BatchImportJobStepProgress.vue';

const props = defineProps({
  jobStatus: {
    type: BatchJobExecutionDetails,
    default: () => new BatchJobExecutionDetails(),
  },
  uploadState: {
    type: String as PropType<BatchImportStepState>,
    default: 'NOT_STARTED',
  },
  processState: {
    type: String as PropType<BatchImportStepState>,
    default: 'NOT_STARTED',
  },
  errorDocument: {
    type: DocumentReference,
    default: undefined,
  },
  loadingErrorDocument: {
    type: Boolean,
    default: false,
  },
});

const {
  jobStatus,
  uploadState,
  processState,
  errorDocument,
  loadingErrorDocument,
} = toRefs(props);

const { t } = useI18n();

const jobStatusTotalRecords = computed((): number => {
  return props.jobStatus.totalRecords ? props.jobStatus.totalRecords : 0;
});
const jobStatusSuccessCount = computed((): number => {
  return props.jobStatus.successCount ? props.jobStatus.successCount : 0;
});
const jobStatusErrorCount = computed((): number => {
  return props.jobStatus.errorCount ? props.jobStatus.errorCount : 0;
});
</script>

<style>
.batch-import-status-section {
  .job-stat {
    font-size: 1.2em;
    color: var(--gray-darkest);
  }
}
</style>
